import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

interface PayloadDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  payload: unknown
  title?: string
}

/**
 * `<PayloadDialog />` — read-only viewer for an `audit_log.payload` jsonb.
 *
 * Story 3.17 §AC #5 — table cell renders a "Ver" button that opens this
 * dialog with the formatted JSON. Sensitive keys are already masked
 * server-side via `redactSensitive`, so the UI doesn't need to redact again.
 */
export function PayloadDialog({
  open,
  onOpenChange,
  payload,
  title = 'Payload',
}: PayloadDialogProps): React.ReactElement {
  const formatted = formatPayload(payload)

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-50 bg-black/60',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 max-h-[85vh] w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 overflow-y-auto',
            'rounded-lg border bg-background p-6 shadow-lg',
          )}
        >
          <div className="flex items-start justify-between">
            <DialogPrimitive.Title className="text-lg font-semibold">
              {title}
            </DialogPrimitive.Title>
            <DialogPrimitive.Close
              className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring"
              aria-label="Fechar"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </DialogPrimitive.Close>
          </div>
          <DialogPrimitive.Description className="mt-1 text-xs text-muted-foreground">
            Chaves sensíveis (token / password / secret) já estão mascaradas.
          </DialogPrimitive.Description>

          <pre className="mt-4 max-h-[60vh] overflow-auto rounded bg-muted/40 p-3 text-xs">
            {formatted}
          </pre>

          <div className="mt-4 flex justify-end">
            <DialogPrimitive.Close asChild>
              <Button type="button" variant="outline">
                Fechar
              </Button>
            </DialogPrimitive.Close>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

function formatPayload(value: unknown): string {
  if (value === null || value === undefined) return 'null'
  if (typeof value === 'string') return value
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return String(value)
  }
}

export default PayloadDialog
